/**
 * @fileoverview Shared inspection template detail slideout panel.
 * @module ui-admin/template-builder/slideout/TemplateDetailSlideoutPanel
 *
 * Responsibilities:
 * - Render common inspection-template grid slideout details and actions.
 * - Provide reusable sections for status, general info, parameters, forms, rules, and audit slots.
 *
 * Dependencies:
 * - React state/callback hooks.
 * - lucide-react icons.
 * - ui-admin InlineEditableField and template type contracts.
 *
 * Architecture:
 * - Presentation component only; host apps supply Dexie-backed template records and mutation callbacks.
 * - Inline edits call host local-first services through callbacks and never call APIs directly.
 *
 * Notes:
 * - Host-specific sections such as system library settings or tenant sharing can be supplied as slots.
 */

import { useCallback, useState, type ReactNode } from 'react';
import {
  Archive,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  Globe,
  Lock,
  PenTool,
  Send,
  Trash2,
  Users,
  Wifi,
  WifiOff,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { PanelTabs } from '../../PanelTabs';
import type { PanelTab } from '../../PanelTabs';
import { InlineEditableField } from '../../InlineEditableField';
import { getIconComponent } from '../../IconPicker';
import type { TemplateBuilderForm, TemplateBuilderTemplate, TemplateStatus, TemplateType } from '../../../types/templateTypes';
import { TEMPLATE_TYPE_LABELS } from '../../../types/templateTypes';
import type { GridTheme } from '../../../contexts/ThemeContext';

const STATUS_DOT: Record<TemplateStatus, string> = {
  draft: 'bg-amber-400',
  published: 'bg-green-500',
  archived: 'bg-gray-400',
};

const TYPE_COLORS: Partial<Record<TemplateType, string>> = {
  wildland: 'bg-green-100 text-green-700',
  fireCode: 'bg-red-100 text-red-700',
  hydrant: 'bg-blue-100 text-blue-700',
  complex: 'bg-purple-100 text-purple-700',
  communityRisk: 'bg-amber-100 text-amber-700',
  custom: 'bg-gray-100 text-gray-700',
};

const TYPE_ICONS: Partial<Record<TemplateType, LucideIcon>> = {
  custom: FileText,
};


/**
 * @contract TemplateDetailSlideoutPanelProps
 *
 * Purpose:
 * - Configures the shared template detail slideout used by system-web and tenant-web grids.
 *
 * Fields:
 * - template: Dexie-backed template row to display.
 * - loadingDetails: Optional flag for local detail reads still in progress.
 * - formsById: Optional Dexie-backed form payload map for rich form rows.
 * - formNames: Optional fallback map for form display names.
 * - librarySlot: Optional host-specific library/sharing section.
 * - auditSlot: Optional host-specific audit timeline panel.
 * - onDescriptionSave: Optional Dexie-first inline description save callback.
 * - onTemplateHeaderEnabledSave: Optional tenant Dexie-first header toggle callback.
 * - onEdit/onPublish/onDuplicate/onArchive/onDelete: Host action callbacks (shell owns Close).
 *
 * Rules:
 * - Mutation callbacks must write through host local-first services.
 *
 * Sync:
 * - Background sync is owned by host services after local writes.
 */
export interface TemplateDetailSlideoutPanelProps {
  template: TemplateBuilderTemplate;
  loadingDetails?: boolean;
  formsById?: ReadonlyMap<string, TemplateBuilderForm>;
  formNames?: ReadonlyMap<string, string>;
  librarySlot?: ReactNode;
  auditSlot?: ReactNode;
  theme?: Partial<GridTheme>;
  onDescriptionSave?: (description: string) => Promise<void> | void;
  onTemplateHeaderEnabledSave?: (enabled: boolean) => Promise<void> | void;
  onEdit: () => void;
  onPublish: (id: string) => void;
  onDuplicate: (id: string) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  /**
   * Mutation affordance gates, keyed by the permission each action needs.
   * `canEdit` hides Edit/Archive and disables inline field edits;
   * `canPublish` hides Publish independently when publication would write
   * attached forms the actor cannot edit;
   * `canDuplicate` hides Duplicate (it creates a new document);
   * `canDelete` hides Delete. All default to true — hosts pass false when
   * the actor lacks the matching permission grant.
   */
  canEdit?: boolean;
  canPublish?: boolean;
  canDuplicate?: boolean;
  canDelete?: boolean;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return formatDate(dateStr);
}

function ParamRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value?: boolean }) {
  const enabled = value === true;
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${enabled ? 'text-green-500' : 'text-gray-300'}`} />
      <span className={enabled ? 'text-gray-700' : 'text-gray-400'}>{label}</span>
      {enabled ? (
        <CheckCircle2 className="h-3 w-3 text-green-500 ml-auto shrink-0" />
      ) : (
        <XCircle className="h-3 w-3 text-gray-300 ml-auto shrink-0" />
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  isOpen,
  onToggle,
  onEdit,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</span>
        <div className="flex items-center gap-2">
          {onEdit && isOpen && (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => { event.stopPropagation(); onEdit(); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onEdit();
                }
              }}
              className="text-[10px] text-orange-600 hover:text-orange-700 font-medium"
            >
              Edit
            </span>
          )}
          <ChevronRight className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

/**
 * @component TemplateDetailSlideoutPanel
 *
 * Purpose:
 * - Provides the canonical grid slideout detail panel for inspection templates.
 *
 * Props:
 * - See TemplateDetailSlideoutPanelProps.
 *
 * Behavior:
 * - Renders common details and delegates all actions/mutations to host callbacks.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not import api clients or perform persistence directly.
 *
 * Performance:
 * - Keeps only expanded section and expanded form state locally.
 */
export function TemplateDetailSlideoutPanel({
  template,
  loadingDetails = false,
  formsById,
  formNames,
  librarySlot,
  auditSlot,
  theme,
  onDescriptionSave,
  onTemplateHeaderEnabledSave,
  onEdit,
  onPublish,
  onDuplicate,
  onArchive,
  onDelete,
  canEdit = true,
  canPublish = canEdit,
  canDuplicate = true,
  canDelete = true,
}: TemplateDetailSlideoutPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['general']));
  const [expandedFormId, setExpandedFormId] = useState<string | null>(null);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => (prev.has(key) ? new Set() : new Set([key])));
  };

  const handleInlineSave = useCallback(async (fieldKey: string, newValue: unknown) => {
    if (!canEdit) return;
    if (fieldKey === 'description') {
      await onDescriptionSave?.((newValue as string) || '');
    }
    if (fieldKey === 'templateHeader.enabled') {
      await onTemplateHeaderEnabledSave?.(newValue === true || newValue === 'true');
    }
  }, [onDescriptionSave, onTemplateHeaderEnabledSave, canEdit]);

  const colorCls = TYPE_COLORS[template.templateType] ?? 'bg-gray-100 text-gray-700';
  const parameters = template.parameters;
  const formRefs = [...(template.formRefs ?? [])].sort((a, b) => a.order - b.order);
  const formCount = formRefs.length;
  const rules = template.rules ?? [];
  const detailTabs: PanelTab[] = [
    {
      id: 'details',
      label: 'Details',
      content: (
        <div>
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className={`h-2 w-2 rounded-full ${STATUS_DOT[template.status] ?? 'bg-gray-400'}`} />
                <span className="text-xs font-medium text-gray-700">
                  {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
                </span>
                <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium ${colorCls}`}>
                  {TEMPLATE_TYPE_LABELS[template.templateType] ?? template.templateType}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {canPublish && template.status === 'draft' && (
                  <button
                    onClick={() => onPublish(template._id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md text-white transition-colors bg-orange-600 hover:bg-orange-700"
                  >
                    <Send className="h-3 w-3" /> Publish
                  </button>
                )}
                {canDuplicate && (
                <button
                  onClick={() => onDuplicate(template._id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Copy className="h-3 w-3" /> Duplicate
                </button>
                )}
                {canEdit && template.status === 'published' && (
                  <button
                    onClick={() => onArchive(template._id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Archive className="h-3 w-3" /> Archive
                  </button>
                )}
                {canDelete && (
                <button
                  onClick={() => onDelete(template._id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors ml-auto"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
                )}
              </div>
            </div>

            <CollapsibleSection title="General Info" isOpen={expandedSections.has('general')} onToggle={() => toggleSection('general')}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                <InlineEditableField
                  label="Description"
                  value={template.description || ''}
                  span={2}
                  config={{ fieldKey: 'description', editType: 'textarea', placeholder: 'Add a description...' }}
                  onSave={handleInlineSave}
                  disabled={!canEdit}
                />
                <div>
                  <span className="block font-medium text-gray-500 mb-0.5">Created</span>
                  <span className="text-gray-700">{formatDate(template.createdAt)}</span>
                </div>
                <div>
                  <span className="block font-medium text-gray-500 mb-0.5">Updated</span>
                  <span className="text-gray-700">{timeAgo(template.updatedAt)}</span>
                </div>
                {template.publishedAt && (
                  <div>
                    <span className="block font-medium text-gray-500 mb-0.5">Published</span>
                    <span className="text-gray-700">{formatDate(template.publishedAt)}</span>
                  </div>
                )}
                <div>
                  <span className="block font-medium text-gray-500 mb-0.5">Version</span>
                  <span className="text-gray-700">v{template.version}</span>
                </div>
                {onTemplateHeaderEnabledSave && (
                  <InlineEditableField
                    label="Template Header"
                    value={template.templateHeader?.enabled ? 'Enabled' : 'Off'}
                    editValue={template.templateHeader?.enabled ?? false}
                    config={{ fieldKey: 'templateHeader.enabled', editType: 'boolean' }}
                    onSave={handleInlineSave}
                    disabled={!canEdit}
                  />
                )}
              </div>
            </CollapsibleSection>

            {librarySlot}

            {loadingDetails ? (
              <div className="px-4 py-4 text-xs text-gray-400 italic">Loading template details...</div>
            ) : parameters && (
              <CollapsibleSection title="Parameters" isOpen={expandedSections.has('parameters')} onToggle={() => toggleSection('parameters')} onEdit={onEdit}>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Reinspection</h5>
                    <div className="space-y-1">
                      <ParamRow icon={Clock} label="Auto-create reinspection" value={parameters.autoCreateReinspectionWhenViolations} />
                      {parameters.maxReinspections != null && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 pl-5">
                          <span className="text-gray-400">Max:</span> {parameters.maxReinspections}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Permissions</h5>
                    <div className="space-y-1">
                      <ParamRow icon={CheckCircle2} label="Allow extensions" value={parameters.allowExtensions} />
                      <ParamRow icon={CheckCircle2} label="Allow exceptions" value={parameters.allowExceptions} />
                      <ParamRow icon={CheckCircle2} label="Enable severity" value={parameters.enableSeverity} />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Self-Certification</h5>
                    <div className="space-y-1">
                      <ParamRow icon={Users} label="Self-certification" value={parameters.selfCertificationEnabled} />
                      {parameters.selfCertificationEnabled && (
                        <ParamRow icon={CheckCircle2} label="Requires photos" value={parameters.selfCertificationRequiresPhotos} />
                      )}
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Notifications</h5>
                    <div className="space-y-1">
                      <ParamRow icon={Send} label="Email owner on complete" value={parameters.emailOwnerOnComplete} />
                      <ParamRow icon={Send} label="Email inspector summary" value={parameters.emailInspectorSummary} />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Signatures</h5>
                    <div className="space-y-1">
                      <ParamRow icon={PenTool} label="Inspector signature" value={parameters.requireInspectorSignature} />
                      <ParamRow icon={PenTool} label="Responsible party signature" value={parameters.requireResponsiblePartySignature} />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600 mb-1.5">Mobile</h5>
                    <div className="space-y-1">
                      <ParamRow icon={parameters.allowOfflineMode ? Wifi : WifiOff} label="Offline mode" value={parameters.allowOfflineMode} />
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            )}

            <CollapsibleSection title={`Forms (${formCount})`} isOpen={expandedSections.has('forms')} onToggle={() => toggleSection('forms')}>
              {formCount === 0 ? (
                <p className="text-xs text-gray-400 italic">No forms attached</p>
              ) : (
                <div className="space-y-1.5">
                  {formRefs.map((ref, idx) => {
                    const form = formsById?.get(ref.formId);
                    const IconComp = form?.display?.icon ? getIconComponent(form.display.icon) : null;
                    const isExpanded = expandedFormId === ref.formId;
                    const sectionCount = form?.layout?.sections?.length ?? 0;
                    return (
                      <div key={ref.formId}>
                        <div
                          onClick={() => setExpandedFormId(isExpanded ? null : ref.formId)}
                          className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100/80 transition-colors"
                        >
                          <span className="text-xs font-medium text-gray-400 w-5 text-center shrink-0">{idx + 1}</span>
                          <div
                            className="w-6 h-6 rounded-sm flex items-center justify-center bg-orange-100 text-orange-600 text-[9px] font-bold shrink-0"
                            style={form?.display?.color ? { backgroundColor: form.display.color, color: '#fff' } : undefined}
                          >
                            {IconComp ? <IconComp size={12} /> : (form?.display?.abbreviation || <FileText size={12} />)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-700 truncate block">{form?.name || formNames?.get(ref.formId) || ref.formId}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 shrink-0">v{ref.formVersion}</span>
                          {form && ref.formVersion < form.version && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 shrink-0" title={`Form updated to v${form.version} — open template to upgrade`}>
                              → v{form.version}
                            </span>
                          )}
                          {ref.optional && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-600 font-medium shrink-0">Optional</span>
                          )}
                          <ChevronRight size={12} className={`text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                        {isExpanded && form && (
                          <div className="ml-7 mt-1 mb-1 pl-3 border-l-2 border-gray-200 space-y-1">
                            {form.description && <p className="text-[10px] text-gray-500">{form.description}</p>}
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
                              <span>{form.fields?.length ?? 0} fields</span>
                              <span>{sectionCount} section{sectionCount !== 1 ? 's' : ''}</span>
                              {form.category && <span>{form.category}</span>}
                              <span className="capitalize">{form.status}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection title={`Rules (${rules.length})`} isOpen={expandedSections.has('rules')} onToggle={() => toggleSection('rules')}>
              {rules.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No rules configured</p>
              ) : (
                <div className="space-y-1.5">
                  {rules.map((rule) => (
                    <div key={rule.id} className="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-100">
                      <div className="text-xs font-medium text-gray-700">{rule.name || rule.id}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        Scope: {rule.scope} · {rule.then.length} action{rule.then.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
      ),
    },
    ...(auditSlot ? [{
      id: 'audit',
      label: 'Audit Log',
      content: (<div className="px-4 py-3">{auditSlot}</div>),
    }] : []),
  ];

  // The hosting GridPageShell owns the panel header (title/Close/Edit) —
  // this component renders only the contracted tab sections.
  return (
    <div className="flex flex-col h-full" style={{ background: theme?.cardBg, color: theme?.textPrimary }}>
      <PanelTabs tabs={detailTabs} ariaLabel="Template detail sections" />
    </div>
  );
}

export default TemplateDetailSlideoutPanel;
